import { useCallback, useState } from 'react'
import { openingTab } from './rules'
import type { WatchTab } from './types'
import { useHeld } from './useMediaQuery'

export interface PhoneTab {
  /** The panel under the video. */
  tab: WatchTab
  /** The viewer picked a tab: from now on it is theirs, and the opening rule no longer moves it. */
  pick(tab: WatchTab): void
  /** Something needs the chat on screen (a sign-in to chat, a chat intent completing): opens Chat. */
  showChat(): void
}

interface PhoneTabOptions {
  hasLiveHost: boolean
  hasMap: boolean
  unframed?: boolean
  /** The stage's call is a host's map: the Map opens (until the viewer picks a tab). */
  mapCalled?: boolean
  /** Keep the tab on screen where it is: the viewer is typing (`useTypingOnScreen`), and a switch would hide the field. */
  hold?: boolean
}

/**
 * The phone's open tab. Until the viewer picks one it follows `openingTab`: Chat while someone is hosting, the Map
 * while nobody is (Chat when there is nothing to map, or nothing for the map to frame yet), so a host going live later
 * opens the chat, a host calling their map opens the map, and a located camera first showing on an unframed map opens the map. Once picked, it stays where the
 * viewer put it. While `hold` is set the opening rule waits: a camera going live or a host leaving mid-message doesn't
 * hide the composer (and close the keyboard); the tab follows the rule again once released.
 */
export function usePhoneTab({ hasLiveHost, hasMap, unframed = false, mapCalled = false, hold = false }: PhoneTabOptions): PhoneTab {
  const [picked, setPicked] = useState<WatchTab | null>(null)
  const pick = useCallback((tab: WatchTab) => setPicked(tab), [])
  const showChat = useCallback(() => setPicked('chat'), [])
  const opening = useHeld(openingTab({ hasLiveHost, hasMap, unframed, mapCalled }), hold)
  return { tab: picked ?? opening, pick, showChat }
}
